import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Clock, Users, CheckCircle, UserPlus, Calendar, Search } from 'lucide-react';

interface QueueClient {
  id: string;
  name: string;
  service: string;
  barber?: string;
  checkInTime: Date;
  status: 'waiting' | 'in-service' | 'completed';
  estimatedWait?: number;
}

const CheckInPage: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { user } = useAuth();
  const navigate = useNavigate();

  const [queue, setQueue] = useState<QueueClient[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [checkInMode, setCheckInMode] = useState<'appointment' | 'walkin'>('appointment');
  const [searchPhone, setSearchPhone] = useState('');

  useEffect(() => {
    // Load queue from localStorage
    const storedQueue = localStorage.getItem('checkInQueue');
    if (storedQueue) {
      setQueue(JSON.parse(storedQueue));
    }
  }, []);

  const handleCheckIn = (type: 'appointment' | 'walkin') => {
    setCheckInMode(type);
    // Navigate to appropriate flow
    if (type === 'appointment') {
      navigate('/booking');
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Clock className="w-7 h-7 text-indigo-600" />
          Check-In & Queue
        </h1>
        <p className="text-gray-600 mt-1">Manage walk-ins and appointments</p>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        {[
          { label: 'In Queue', value: queue.filter(c => c.status === 'waiting').length, icon: Users, color: 'blue' },
          { label: 'In Service', value: queue.filter(c => c.status === 'in-service').length, icon: Clock, color: 'green' },
          { label: 'Completed', value: queue.filter(c => c.status === 'completed').length, icon: CheckCircle, color: 'purple' },
          { label: 'Avg Wait', value: '15 min', icon: Clock, color: 'orange' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <stat.icon className={`w-5 h-5 text-${stat.color}-500`} />
            </div>
            <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
            <p className="text-xs text-gray-500 mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Check-In Options */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-6">
          <h2 className="text-lg md:text-xl font-semibold mb-4 text-gray-900">Check-In Options</h2>

          {/* Phone Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="tel"
              placeholder="Search by phone number..."
              value={searchPhone}
              onChange={(e) => setSearchPhone(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={() => handleCheckIn('appointment')}
              className="w-full flex items-center justify-center gap-3 py-4 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all touch-manipulation min-h-[56px]"
            >
              <Calendar className="w-5 h-5" />
              <span>Appointment Check-In</span>
            </button>
            <button
              onClick={() => handleCheckIn('walkin')}
              className="w-full flex items-center justify-center gap-3 py-4 px-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all touch-manipulation min-h-[56px]"
            >
              <UserPlus className="w-5 h-5" />
              <span>Walk-In Registration</span>
            </button>
          </div>

          {/* Quick Actions */}
          <div className="mt-6 pt-4 border-t border-gray-100">
            <p className="text-sm text-gray-500 mb-3">Quick Actions</p>
            <div className="grid grid-cols-2 gap-2">
              <button className="p-3 bg-gray-50 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors touch-manipulation min-h-[44px]">
                View Schedule
              </button>
              <button className="p-3 bg-gray-50 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors touch-manipulation min-h-[44px]">
                Add to Queue
              </button>
            </div>
          </div>
        </div>

        {/* Current Queue */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg md:text-xl font-semibold text-gray-900">Current Queue</h2>
            <span className="text-sm text-gray-500">{queue.filter(c => c.status === 'waiting').length} waiting</span>
          </div>

          <div className="space-y-3">
            {queue.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No clients in queue</p>
                <p className="text-sm text-gray-400 mt-1">Check in a client to get started</p>
              </div>
            ) : (
              queue
                .filter(c => c.status === 'waiting')
                .map((client) => (
                  <div
                    key={client.id}
                    className="p-4 bg-gray-50 rounded-xl border border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{client.name}</p>
                      <p className="text-sm text-gray-500">{client.service}</p>
                      {client.estimatedWait && (
                        <p className="text-xs text-indigo-600 mt-1">~{client.estimatedWait} min wait</p>
                      )}
                    </div>
                    <button className="px-4 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors touch-manipulation min-h-[44px]">
                      Start Service
                    </button>
                  </div>
                ))
            )}
          </div>

          {/* In Service Section */}
          {queue.filter(c => c.status === 'in-service').length > 0 && (
            <div className="mt-6 pt-4 border-t border-gray-100">
              <p className="text-sm font-medium text-gray-700 mb-3">Currently In Service</p>
              {queue
                .filter(c => c.status === 'in-service')
                .map((client) => (
                  <div
                    key={client.id}
                    className="p-3 bg-green-50 rounded-xl border border-green-100 flex items-center justify-between"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{client.name}</p>
                      <p className="text-sm text-green-600">{client.service} with {client.barber}</p>
                    </div>
                    <button className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-medium touch-manipulation min-h-[40px]">
                      Complete
                    </button>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckInPage;
